/**
 * Utility to detect if the application is running in a WebView environment
 */

/**
 * Detects if the current environment is a React Native WebView
 * @returns boolean indicating if we're in a React Native WebView
 */

interface BeCreativesWindowConfig {
  beCreativesConfig?: {
    isWebView?: boolean;
    brandId?: string;
    isCreateRequest?: boolean;
    brainTab?: string;
    isMobileOnboarding?: boolean;
    allowBrandCreationWithoutSubscription?: boolean;
    isOnboarding?: boolean;
  };
}

export const getWebviewConfig = () => {
  return (window as BeCreativesWindowConfig)?.beCreativesConfig;
};

export const getBrandIdFromWebviewConfig = () => {
  return getWebviewConfig()?.brandId;
};

export const getIsCreateRequestFromWebviewConfig = () => {
  return getWebviewConfig()?.isCreateRequest;
};

export const getBrainTabFromWebviewConfig = () => {
  return getWebviewConfig()?.brainTab;
};

export const isReactNativeWebView = (): boolean => {
  // Check for specific properties that React Native WebView might inject
  if (typeof window === 'undefined') {
    return false;
  }

  // Check for ReactNativeWebView object
  const hasWebViewObject: boolean = 'ReactNativeWebView' in window;
  const isBeCreativesWebView = !!(window as BeCreativesWindowConfig)?.beCreativesConfig?.isWebView;

  // Check for WebView in user agent
  let hasWebViewUserAgent = false;
  if (typeof navigator !== 'undefined' && navigator.userAgent) {
    hasWebViewUserAgent = /wv|ReactNativeWebView/.test(navigator.userAgent);
  }

  return (hasWebViewObject || hasWebViewUserAgent) && isBeCreativesWebView;
};

/**
 * Detects if we have a message channel established with a parent React Native app
 * @returns boolean indicating if communication channel is available
 */
export const hasReactNativeMessageChannel = (): boolean => {
  return typeof window !== 'undefined' && 'ReactNativeWebView' in window;
};

/**
 * Checks if the mobile app onboarding bypass is allowed
 * Uses triple verification for security:
 * 1. Must have ReactNativeWebView object (RN WebView)
 * 2. Must have beCreatives config with isWebView flag
 * 3. Must have at least one mobile onboarding flag:
 *    - isMobileOnboarding
 *    - allowBrandCreationWithoutSubscription
 *    - isOnboarding
 *
 * @returns boolean indicating if mobile onboarding bypass is allowed
 */
export const isMobileAppOnboardingAllowed = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  // 1. Must have ReactNativeWebView object (injected by RN WebView)
  const hasWebViewObject = 'ReactNativeWebView' in window;

  // 2. Must have beCreatives config with isWebView flag
  const config = (window as BeCreativesWindowConfig)?.beCreativesConfig;
  const isBeCreativesWebView = !!config?.isWebView;

  // 3. Must have specific mobile onboarding flag OR allowBrandCreationWithoutSubscription
  const isMobileOnboarding =
    !!config?.isMobileOnboarding ||
    !!config?.allowBrandCreationWithoutSubscription ||
    !!config?.isOnboarding;

  return hasWebViewObject && isBeCreativesWebView && isMobileOnboarding;
};

/**
 * Gets the mobile onboarding config status
 * @returns boolean indicating if mobile onboarding is enabled
 */
export const getMobileOnboardingConfig = (): boolean => {
  const config = getWebviewConfig();
  return (
    !!config?.isMobileOnboarding ||
    !!config?.allowBrandCreationWithoutSubscription ||
    !!config?.isOnboarding
  );
};
